import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GlobalSidebar from '../components/GlobalSidebar';
import GlobalEmptyState from '../components/GlobalEmptyState';
import { GlobalSortableTh } from '../components/GlobalSortableTh';
import { GlobalButton } from '../components/GlobalButton';
import { BusinessOpportunityForm } from '../components/BusinessOpportunityForm';
import { StatCard } from '../components/StatCard';
import { SortableStatGrid } from '../components/SortableStatGrid';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { ViewLayoutToggle } from '../components/ViewLayoutToggle';
import { ViewSections, type ViewSection } from '../components/ViewSections';
import { useTableSort } from '../hooks/useTableSort';
import { useViewLayoutMode } from '../hooks/useViewLayoutMode';
import { useAppData } from '../context/AppDataContext';
import { useOpportunityDiscovery } from '../context/OpportunityDiscoveryContext';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import { saveBusinessOpportunity } from '../lib/saveBusinessOpportunity';
import { businessOpportunityStatusLabel } from '../utils/businessOpportunity';
import { formatDateLong } from '../utils/formatters';
import type { BusinessOpportunity } from '../types';

type OpportunitySortKey = 'name' | 'status' | 'industry' | 'updatedAt' | 'analysis' | 'pnl';

export function BusinessOpportunitiesView() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { mode: layoutMode, toggle: toggleLayout } = useViewLayoutMode();
  const { user } = useUser();
  const userId = user?.uid ?? '';
  const { businessOpportunities, businessOpportunitiesLoading, businessOpportunitiesError } = useAppData();
  const opportunities = useMemo(() => businessOpportunities ?? [], [businessOpportunities]);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { discovering, startDiscovery } = useOpportunityDiscovery();

  const { sortKey, sortDirection, toggleSort, sortRows } = useTableSort<OpportunitySortKey>(
    'updatedAt',
    'desc'
  );

  const sorted = useMemo(() => {
    return sortRows(opportunities, {
      name: (o) => o.name,
      status: (o) => o.status,
      industry: (o) => o.industry ?? '',
      updatedAt: (o) => o.updatedAt,
      analysis: (o) => (o.ideaIntelligence ? 1 : 0),
      pnl: (o) => (o.pnl ? 1 : 0),
    });
  }, [opportunities, sortRows]);

  const summary = useMemo(() => {
    const total = opportunities.length;
    const refiningCount = opportunities.filter((o) => o.status === 'refining').length;
    const validatedCount = opportunities.filter((o) => o.status === 'validated').length;
    const analyzedCount = opportunities.filter((o) => o.ideaIntelligence).length;
    return { total, refiningCount, validatedCount, analyzedCount };
  }, [opportunities]);

  async function saveNewOpportunity(next: BusinessOpportunity) {
    if (saving) return;
    if (!userId) {
      setSaveError('Sign in to add opportunities.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await saveBusinessOpportunity(userId, {
        name: next.name,
        industry: next.industry,
        ideaSummary: next.ideaSummary,
        status: next.status,
      });
      setAddOpen(false);
      navigate(`/opportunities/${saved.id}`);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save opportunity.');
    } finally {
      setSaving(false);
    }
  }

  const toolbarActions = (
    <>
      <ViewLayoutToggle mode={layoutMode} onToggle={toggleLayout} />
      <GlobalButton
        type="button"
        variant="secondary"
        disabled={discovering || saving}
        onClick={() => void startDiscovery()}
        icon={
          <img
            src={isDarkMode ? '/assets/icons/apple.intelligence.svg' : '/assets/icons/apple.intelligence.green.svg'}
            alt=""
            width={16}
            height={16}
          />
        }
      >
        {discovering ? 'Discovering…' : 'Discover idea'}
      </GlobalButton>
      <GlobalButton
        type="button"
        variant="secondary"
        disabled={discovering || saving}
        onClick={() => setAddOpen(true)}
        icon={
          <img
            src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
            alt=""
            width={16}
            height={16}
          />
        }
      >
        Add opportunity
      </GlobalButton>
    </>
  );

  const summarySection = (
    <>
      <ViewSectionHeader
        eyebrow="Opportunities"
        title="Summary"
        subtitle="Forge early ideas into validated opportunities before they enter dealflow."
      />
      <SortableStatGrid
        viewId="business-opportunities"
        className="grid-stats grid-stats-2"
        cards={[
          {
            id: 'total',
            pinned: true,
            label: 'Ideas',
            node: (
              <StatCard
                label="Ideas"
                emoji="💡"
                value={`${summary.total} ${summary.total === 1 ? 'idea' : 'ideas'}`}
                hint={summary.total > 0 ? 'Your pre-dealflow pipeline' : 'Capture rough ideas here first'}
              />
            ),
          },
          {
            id: 'refining',
            label: 'Refining',
            node: (
              <StatCard
                label="Refining"
                emoji="🔥"
                value={String(summary.refiningCount)}
                hint="Ideas actively being forged"
              />
            ),
          },
          {
            id: 'validated',
            label: 'Validated',
            node: (
              <StatCard
                label="Validated"
                emoji="✅"
                value={String(summary.validatedCount)}
                hint="Ready to promote into dealflow"
              />
            ),
          },
          {
            id: 'analyzed',
            label: 'Analyzed',
            node: (
              <StatCard
                label="Analyzed"
                emoji="🧠"
                value={`${summary.analyzedCount}/${summary.total || 0}`}
                hint="Ideas with AI due diligence"
              />
            ),
          },
        ]}
      />
    </>
  );

  const listSection = (
    <>
      <ViewSectionHeader
        eyebrow="Opportunities"
        title="Ideas"
        subtitle="Analyze, refine, and model P&L before promoting to dealflow."
      />
      <section>
        {businessOpportunitiesLoading ? (
          <p className="muted">Loading opportunities…</p>
        ) : sorted.length === 0 ? (
          <GlobalEmptyState
            buttonText={discovering ? 'Discovering…' : 'Discover idea'}
            onButtonClick={() => void startDiscovery()}
          />
        ) : (
          <div className="table-card">
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <GlobalSortableTh sortKey="name" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Name
                    </GlobalSortableTh>
                    <GlobalSortableTh sortKey="status" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Status
                    </GlobalSortableTh>
                    <GlobalSortableTh sortKey="industry" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Industry
                    </GlobalSortableTh>
                    <GlobalSortableTh sortKey="analysis" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Analysis
                    </GlobalSortableTh>
                    <GlobalSortableTh sortKey="pnl" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      P&L
                    </GlobalSortableTh>
                    <GlobalSortableTh sortKey="updatedAt" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                      Updated
                    </GlobalSortableTh>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((o) => (
                    <tr
                      key={o.id}
                      className="click-row"
                      onClick={() => navigate(`/opportunities/${o.id}`)}
                    >
                      <td>{o.name}</td>
                      <td>{businessOpportunityStatusLabel(o.status)}</td>
                      <td>{o.industry?.trim() || '—'}</td>
                      <td>{o.ideaIntelligence ? 'Yes' : '—'}</td>
                      <td>{o.pnl ? 'Yes' : '—'}</td>
                      <td>{formatDateLong(o.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </>
  );

  const sections: ViewSection[] = [
    { id: 'summary', label: 'Summary', content: summarySection },
    { id: 'list', label: 'Opportunities', content: listSection },
  ];

  return (
    <>
      <ViewPage title="Opportunities" actions={toolbarActions}>
        {businessOpportunitiesError ? (
          <p className="form-error" role="alert">
            Could not load opportunities: {businessOpportunitiesError}
          </p>
        ) : null}
        <ViewSections mode={layoutMode} sections={sections} />
      </ViewPage>

      <GlobalSidebar isOpen={addOpen} onClose={() => setAddOpen(false)}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Add opportunity</h2>
          {saveError ? <p className="form-error">{saveError}</p> : null}
          <BusinessOpportunityForm
            onSubmit={saveNewOpportunity}
            onCancel={() => setAddOpen(false)}
          />
        </div>
      </GlobalSidebar>
    </>
  );
}
